import { useAtom } from "jotai";
import { useCallback } from "react";
import {
  staffAtom,
  staffUIAtom,
  currentStaffAtom,
  fetchProjectStaffAtom,
  fetchOrgStaffAtom,
  createStaffAtom,
  updateStaffAtom,
  deleteStaffAtom,
  deallocateStaffAtom,
  importStaffAtom,
} from "../atoms/staff_and_plant/satffAtom";

export const useStaffManagement = () => {
  const [staffState] = useAtom(staffAtom);
  const [uiState, setUIState] = useAtom(staffUIAtom);
  const [currentStaff] = useAtom(currentStaffAtom);
  
  // Action atoms
  const [, fetchProjectStaff] = useAtom(fetchProjectStaffAtom);
  const [, fetchOrgStaff] = useAtom(fetchOrgStaffAtom);
  const [, createStaff] = useAtom(createStaffAtom);
  const [, updateStaffAction] = useAtom(updateStaffAtom);
  const [, deleteStaffAction] = useAtom(deleteStaffAtom);
  const [, deallocateStaffAction] = useAtom(deallocateStaffAtom);
  const [, importStaffAction] = useAtom(importStaffAtom);

  // Memoized values
  const { projectStaff, orgStaff, loading, error } = staffState;
  const { activeTab, searchQuery, selectedStaff } = uiState;

  // Load staff based on current tab
  const loadStaff = useCallback(async () => {
    try {
      if (activeTab === 0) {
        await fetchProjectStaff();
      } else {
        await fetchOrgStaff();
      }
    } catch (error) {
      console.error("Error loading staff:", error);
      throw error;
    }
  }, [activeTab, fetchProjectStaff, fetchOrgStaff]);

  // Set active tab
  const setActiveTab = useCallback((tab) => {
    setUIState((prev) => ({ ...prev, activeTab: tab, selectedStaff: [] }));
  }, [setUIState]);

  // Set search query
  const setSearchQuery = useCallback((query) => {
    setUIState((prev) => ({ ...prev, searchQuery: query }));
  }, [setUIState]);

  // Toggle staff selection
  const toggleStaffSelection = useCallback((staffId) => {
    setUIState((prev) => ({
      ...prev,
      selectedStaff: prev.selectedStaff.includes(staffId)
        ? prev.selectedStaff.filter((id) => id !== staffId)
        : [...prev.selectedStaff, staffId],
    }));
  }, [setUIState]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setUIState((prev) => ({ ...prev, selectedStaff: [] }));
  }, [setUIState]);

  // Create staff
  const createStaffMember = useCallback(async (staffData, isOrgOnly = false) => {
    try {
      return await createStaff({ staffData, isOrgOnly });
    } catch (error) {
      console.error("Error creating staff:", error);
      throw error;
    }
  }, [createStaff]);

  // Update staff
  const updateStaffMember = useCallback(async (staffId, staffData) => {
    try {
      return await updateStaffAction({ staffId, staffData });
    } catch (error) {
      console.error("Error updating staff:", error);
      throw error;
    }
  }, [updateStaffAction]);

  // Delete staff
  const deleteStaffMember = useCallback(async (staffId, forceDelete = false) => {
    try {
      return await deleteStaffAction({ staffId, forceDelete });
    } catch (error) {
      console.error("Error deleting staff:", error);
      throw error;
    }
  }, [deleteStaffAction]);

  // Deallocate staff
  const deallocateStaffMember = useCallback(async (staffId, forceDelete = false) => {
    try {
      return await deallocateStaffAction({ staffId, forceDelete });
    } catch (error) {
      console.error("Error deallocating staff:", error);
      throw error;
    }
  }, [deallocateStaffAction]);

  // Import staff
  const importStaffMembers = useCallback(async (staffIds) => {
    try {
      return await importStaffAction(staffIds);
    } catch (error) {
      console.error("Error importing staff:", error);
      throw error;
    }
  }, [importStaffAction]);

  return {
    // State
    currentStaff,
    projectStaff,
    orgStaff,
    isLoading: loading,
    error,
    activeTab,
    searchQuery,
    selectedStaff,

    // Actions
    setActiveTab,
    setSearchQuery,
    toggleStaffSelection,
    clearSelection,
    loadStaff,
    createStaff: createStaffMember,
    updateStaff: updateStaffMember,
    deleteStaff: deleteStaffMember,
    deallocateStaff: deallocateStaffMember,
    importStaff: importStaffMembers,
  };
};